import React, { useEffect, useState } from "react";
import { t, TFunction } from "i18next";
import { showError } from "redux/actions/ui";
import { updateFacilityTeeSheetSettings } from "redux/actions/facility";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import "./teeSheetGeneral.scss";
import { isEqualWith } from "lodash";
import ReactDOM from "react-dom";
import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import ScrollTabsLayout, { TSection } from "components/scrollTabsLayout/ScrollTabsLayout";
import DisplaySettings from "./teeSheetGeneralSections/DisplaySettings";
import BookingConfiguration from "./teeSheetGeneralSections/BookingConfiguration";
import PaymentsAndDeposits from "./teeSheetGeneralSections/PaymentsAndDeposits";
import CartManagement from "./teeSheetGeneralSections/CartManagement";
import CancellationPolicies from "./teeSheetGeneralSections/CancellationPolicies";
import QuickLinks from "./teeSheetGeneralSections/QuickLinks";
import MarketingAndTerms from "./teeSheetGeneralSections/MarketingAndTerms";
import useDebounce from "hooks/events/useDebounce";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";

export default function TeeSheetGeneral() {
  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(state => state.facilityStore);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);

  const [stateBeforeChanges, setStateBeforeChanges] = useState<IPutTeeSheetSettings>(undefined);
  const [state, setState] = useState<IPutTeeSheetSettings>({
    web_cancellation_hours: null,
    web_edit_hours: null,
    enable_web_booking: false,
    max_booking_size: 16,
    display_customer_type: false,
    booking_terms: "",
    payment_terms: "",
    marketing_section: "",
    single_bookings: "",
    twosome_bookings: "",
    threesome_bookings: "",
    foursome_bookings: "",
    enable_cart_bookings: null,
    display_online_pricing: false,
    power_cart_maintenance_time: 0,
    power_cart_nine_hole_time: 0,
    power_cart_eighteen_hole_time: 0,
    default_carts_available: null,
    track_cart_numbers: false,
    use_cart_inventory: false,
    display_turn_division_code: false,
    display_booking_graph: false,
    display_booking_source: false,
    default_open_structure: "",
    default_days_in_advance_start: 0,
    default_days_in_advance_end: 0,
    default_open_time: "",
    cart_waiver_content: "",
    web_tee_times_display: null,
    validate_power_cart_count: false,
    gift_cards_default_auto_generate_code: false,
    rain_check_expiry: "one_year",
    rain_check_expiry_months: 6,
    rain_check_description: "",
    booking_buffer: null,
    highlight_new_bookings: false,
    highlight_new_bookings_window: 0,
    bookings_allow_guest_names: false,
    auto_charge_no_show: false,
    enable_service_fees: false,
    tee_sheet_overview: false,
    tee_times_pre_paid_option: false,
    tee_times_access_code: false,
    send_maintenance_notifications: false,
    maintenance_email: "",
    enable_tee_time_deposits: false,
    deposit_amount: "",
    deposit_application: "player",
    web_display_payment_option_account: false,
    web_display_payment_option_credit_book: false,
    web_display_payment_option_gift_card: false,
    web_display_full_tee_times: false,
    prevent_multiple_bookings: false,
    default_booking_holes: 18,
    auto_refund_pre_paid_bookings: false,
    enable_booking_reminder_emails: false,
    enable_booking_reminder_texts: false,
    booking_reminder_hours: 24,
  });

  const [dates, setDates] = useState<{ open_date: Date; close_date: Date } | undefined>({
    open_date: new Date(),
    close_date: new Date(),
  });

  const { state: dateWarning, updateModal: updateDateWarning, closeModal: closeDateWarning } = useModal();
  const [dateBeforeChanges, setDateBeforeChanges] = useState<{ open_date: Date; close_date: Date }>(undefined);

  //stop unnecessary re-assignment when facilityStore changes
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const settings = facilityStore.teeSheetSettings;

    if (settings && !isLoaded) {
      setState({
        web_cancellation_hours: settings.web_cancellation_hours,
        web_edit_hours: settings.web_edit_hours,
        enable_web_booking: !!settings.enable_web_booking,
        max_booking_size: settings.max_booking_size,
        display_customer_type: !!settings.display_customer_type,
        booking_terms: settings.booking_terms,
        payment_terms: settings.payment_terms,
        marketing_section: settings.marketing_section,
        single_bookings: settings.single_bookings,
        twosome_bookings: settings.twosome_bookings,
        threesome_bookings: settings.threesome_bookings,
        foursome_bookings: settings.foursome_bookings,
        enable_cart_bookings: !!settings.enable_cart_bookings,
        display_online_pricing: !!settings.display_online_pricing,
        power_cart_maintenance_time: settings.power_cart_maintenance_time,
        power_cart_nine_hole_time: settings.power_cart_nine_hole_time,
        power_cart_eighteen_hole_time: settings.power_cart_eighteen_hole_time,
        default_carts_available: settings.default_carts_available,
        track_cart_numbers: !!settings.track_cart_numbers,
        use_cart_inventory: !!settings.use_cart_inventory,
        display_turn_division_code: !!settings.display_turn_division_code,
        display_booking_graph: !!settings.display_booking_graph,
        display_booking_source: !!settings.display_booking_source,
        default_open_structure: settings.default_open_structure,
        default_days_in_advance_start: settings.default_days_in_advance_start,
        default_days_in_advance_end: settings.default_days_in_advance_end,
        default_open_time: settings.default_open_time,
        cart_waiver_content: settings.cart_waiver_content,
        web_tee_times_display: settings.web_tee_times_display,
        validate_power_cart_count: !!settings.validate_power_cart_count,
        gift_cards_default_auto_generate_code: settings.gift_cards_default_auto_generate_code,
        rain_check_expiry: settings.rain_check_expiry,
        rain_check_expiry_months: settings.rain_check_expiry_months ?? 6,
        rain_check_description: settings.rain_check_description,
        booking_buffer: settings.booking_buffer,
        highlight_new_bookings: settings.highlight_new_bookings,
        highlight_new_bookings_window: settings.highlight_new_bookings_window ?? 0,
        bookings_allow_guest_names: settings.bookings_allow_guest_names,
        auto_charge_no_show: settings.auto_charge_no_show,
        enable_service_fees: settings.enable_service_fees,
        tee_sheet_overview: settings.tee_sheet_overview,
        tee_times_pre_paid_option: !!settings.tee_times_pre_paid_option,
        tee_times_access_code: settings.tee_times_access_code,
        send_maintenance_notifications: settings.send_maintenance_notifications,
        maintenance_email: settings.maintenance_email,
        enable_tee_time_deposits: !!settings.enable_tee_time_deposits,
        deposit_amount: settings.deposit_amount ? settings?.deposit_amount?.toString() : "",
        deposit_application: settings.deposit_application,
        web_display_payment_option_account: settings?.web_display_payment_option_account,
        web_display_payment_option_credit_book: settings?.web_display_payment_option_credit_book,
        web_display_payment_option_gift_card: settings?.web_display_payment_option_gift_card,
        web_display_full_tee_times: settings?.web_display_full_tee_times,
        booking_engine_display_waitlist: settings?.booking_engine_display_waitlist,
        prevent_multiple_bookings: settings?.prevent_multiple_bookings,
        default_booking_holes: settings?.default_booking_holes ?? 18,
        auto_refund_pre_paid_bookings: settings?.auto_refund_pre_paid_bookings,
        display_divisions_online: settings?.display_divisions_online,
        enable_booking_reminder_emails: settings?.enable_booking_reminder_emails ?? false,
        enable_booking_reminder_texts: !!settings?.enable_booking_reminder_texts,
        booking_reminder_hours: settings?.booking_reminder_hours ?? 24,
      });

      if (settings.open_date && settings.close_date) {
        const splitOpenDate: string[] = settings.open_date.split("-");
        const splitCloseDate: string[] = settings.close_date.split("-");
        setDates({
          open_date: new Date(parseInt(splitOpenDate[0]), parseInt(splitOpenDate[1]) - 1, parseInt(splitOpenDate[2])),
          close_date: new Date(
            parseInt(splitCloseDate[0]),
            parseInt(splitCloseDate[1]) - 1,
            parseInt(splitCloseDate[2]),
          ),
        });
      }

      setIsLoaded(true);
    }
  }, [facilityStore.teeSheetSettings]);

  function isDateInThePast(date: Date) {
    const now = new Date();
    const isPast = date < now;

    if (isPast) {
      return true;
    }

    return false;
  }

  function saveTeeSheetSettings() {
    if (dates.close_date < dates.open_date) {
      dispatch(showError(t("Open date cannot happen after the close date.")));
      return;
    }

    // arbitrary booking size limits
    if (state.max_booking_size > 256) {
      dispatch(showError(t("Maximum booking size is 256 people.")));
      return;
    } else if (state.max_booking_size < 5) {
      dispatch(showError(t("Minimum booking size is 5 people.")));
      return;
    }

    const putParams: IPutTeeSheetSettings = {
      web_cancellation_hours: state.web_cancellation_hours,
      web_edit_hours: state.web_edit_hours,
      enable_web_booking: state.enable_web_booking,
      enable_cart_bookings: state.enable_cart_bookings,
      max_booking_size: state.max_booking_size,
      open_date: dates.open_date.toISOString().slice(0, 10),
      close_date: dates.close_date.toISOString().slice(0, 10),
      display_customer_type: state.display_customer_type,
      booking_terms: state.booking_terms,
      payment_terms: state.payment_terms,
      marketing_section: state.marketing_section,
      single_bookings: state.single_bookings,
      twosome_bookings: state.twosome_bookings,
      threesome_bookings: state.threesome_bookings,
      foursome_bookings: state.foursome_bookings,
      display_online_pricing: state.display_online_pricing,
      power_cart_maintenance_time: state.power_cart_maintenance_time,
      power_cart_nine_hole_time: state.power_cart_nine_hole_time,
      power_cart_eighteen_hole_time: state.power_cart_eighteen_hole_time,
      default_carts_available: state.default_carts_available,
      track_cart_numbers: state.track_cart_numbers,
      use_cart_inventory: state.use_cart_inventory,
      display_turn_division_code: state.display_turn_division_code,
      display_booking_graph: state.display_booking_graph,
      display_booking_source: state.display_booking_source,
      default_open_structure: state.default_open_structure,
      default_days_in_advance_start: state.default_days_in_advance_start,
      default_days_in_advance_end: state.default_days_in_advance_end,
      default_open_time: state.default_open_time,
      cart_waiver_content: state.cart_waiver_content,
      web_tee_times_display: state.web_tee_times_display,
      validate_power_cart_count: state.validate_power_cart_count,
      gift_cards_default_auto_generate_code: state.gift_cards_default_auto_generate_code,
      rain_check_expiry: state.rain_check_expiry,
      rain_check_expiry_months: state.rain_check_expiry_months ?? 6,
      rain_check_description: state.rain_check_description,
      booking_buffer: state.booking_buffer,
      highlight_new_bookings: state.highlight_new_bookings,
      highlight_new_bookings_window: state.highlight_new_bookings_window,
      bookings_allow_guest_names: state.bookings_allow_guest_names,
      auto_charge_no_show: state.auto_charge_no_show,
      enable_service_fees: state.enable_service_fees,
      tee_sheet_overview: state.tee_sheet_overview,
      tee_times_pre_paid_option: state.tee_times_pre_paid_option,
      tee_times_access_code: state.tee_times_access_code,
      send_maintenance_notifications: state.send_maintenance_notifications,
      maintenance_email: state.maintenance_email,
      enable_tee_time_deposits: state?.enable_tee_time_deposits,
      deposit_amount: state?.deposit_amount ? state?.deposit_amount : "0",
      deposit_application: state?.deposit_application ?? "player",
      web_display_payment_option_account: state?.web_display_payment_option_account,
      web_display_payment_option_credit_book: state?.web_display_payment_option_credit_book,
      web_display_payment_option_gift_card: state?.web_display_payment_option_gift_card,
      web_display_full_tee_times: state?.web_display_full_tee_times,
      booking_engine_display_waitlist: state?.booking_engine_display_waitlist,
      prevent_multiple_bookings: state?.prevent_multiple_bookings,
      default_booking_holes: state?.default_booking_holes ?? 18,
      auto_refund_pre_paid_bookings: state?.auto_refund_pre_paid_bookings,
      display_divisions_online: state?.display_divisions_online,
      enable_booking_reminder_emails: !!state?.enable_booking_reminder_emails,
      enable_booking_reminder_texts: !!state?.enable_booking_reminder_texts,
      booking_reminder_hours: state?.booking_reminder_hours ?? 24,
    };

    ReactDOM.unstable_batchedUpdates(() => {
      setStateBeforeChanges(state);
      setDateBeforeChanges(dates);
      closeDateWarning();
      // setOverrideTeeSheetsBeforeChanges(overrideTeeSheets);
      void dispatch(updateFacilityTeeSheetSettings(putParams, true));
    });
  }

  function unsavedChangesExist() {
    if (stateBeforeChanges === undefined) {
      if (isLoaded) {
        setStateBeforeChanges(state);
        setDateBeforeChanges(dates);
      }
      return false;
    }

    return (
      !isEqualWith(stateBeforeChanges, state) || !isEqualWith(dateBeforeChanges, dates)
      // !isEqualWith(overrideTeeSheetsBeforeChanges, overrideTeeSheets)
    );
  }

  function cancelUnsavedChanges() {
    setState(stateBeforeChanges);
    setDates(dateBeforeChanges);
    // setOverrideTeeSheets(overrideTeeSheetsBeforeChanges);
  }

  const settingsSections: Array<TSection> = [
    {
      id: "display-settings",
      label: t("Display Settings"),
      content: <DisplaySettings state={state} updateState={updateState} />,
      keywords: [
        "display",
        "enable web booking",
        "web booking",
        "online pricing",
        "access code",
        "customer type",
        "booking graph",
        "overview",
        "tee sheet",
        "division code",
        "display turn division code",
        "show full tee times online",
        "waitlist",
        "wait list",
        "single booking per tee time",
        "single booking",
      ],
    },
    {
      id: "booking-configuration",
      label: t("Booking Configuration"),
      content: <BookingConfiguration state={state} updateState={updateState} dates={dates} setDates={setDates} />,
      keywords: [
        "booking configuration",
        "days in advance",
        "open",
        "close",
        "date",
        "booking size",
        "open time",
        "default",
        "booking type",
        "highlight",
        "guest name",
        "9 hole",
        "18 hole",
        "holes",
      ],
    },
    {
      id: "payments-and-deposits",
      label: t("Payments and Deposits"),
      content: <PaymentsAndDeposits state={state} updateState={updateState} />,
      keywords: [
        "payments",
        "deposits",
        "pre paid",
        "amount",
        "application",
        "rain check",
        "expiry",
        "account",
        "gift card",
        "credit book",
      ],
    },
    {
      id: "cart-management",
      label: t("Cart Management"),
      content: <CartManagement state={state} updateState={updateState} />,
      keywords: [
        "carts",
        "management",
        "inventory",
        "power cart",
        "count",
        "maintenance",
        "notifications",
        "email",
        "9 hole",
        "18 hole",
        "time",
        "available",
      ],
    },
    {
      id: "marketing-and-terms",
      label: t("Marketing and Terms"),
      content: <MarketingAndTerms state={state} updateState={updateState} />,
      keywords: ["marketing and terms", "booking terms", "payment terms", "marketing section"],
    },
    {
      id: "cancellation-policies",
      label: t("Cancellation Policies"),
      content: <CancellationPolicies state={state} updateState={updateState} />,
      keywords: [
        "cancellation policies",
        "cancellation",
        "cancellation window",
        "edit window",
        "window",
        "no show",
        "auto charge no show",
      ],
    },
    {
      id: "quick-links",
      label: t("Quick Links"),
      content: <QuickLinks />,
      keywords: ["quick links", "booking url", "customer url", "url"],
    },
  ];

  function sectionMatchesSearch(section: TSection, query: string, t: TFunction) {
    if (!query) {
      return true;
    }

    const searchableText = [t(section?.label), ...section.keywords.map(k => t(k))]?.join(" ");

    return query
      ?.toLowerCase()
      ?.split(/\s+/)
      ?.every(word => searchableText.includes(word));
  }

  function updateState<K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) {
    setState(prev => ({ ...prev, [key]: value }));
  }

  const filteredSections = settingsSections.filter(section => sectionMatchesSearch(section, debouncedSearch, t));

  return (
    <Page
      title={t("General Tee Sheet Settings")}
      subtitle={t("Manage global settings for the admin tee sheet and public booking site")}
      notificationBarProps={{
        isVisible: !dateWarning.isOpen ? unsavedChangesExist() : false,
        onAction: () =>
          isDateInThePast(dates.close_date) ? updateDateWarning({ isOpen: true }) : saveTeeSheetSettings(),
        onCancel: cancelUnsavedChanges,
      }}
      headerContent={
        <Input
          className="bg-white"
          labelHidden
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t("Search settings...")}
          onSearchClear={() => setSearchQuery("")}
        />
      }
    >
      <div className="ui-tee-sheet-general-scroll-container">
        <ScrollTabsLayout
          showSectionTitles
          sections={filteredSections}
          emptyState={{
            title: t("Setting Not Found"),
            content: t("No matching setting found. Please readjust your search and try again."),
          }}
        />
      </div>

      <Portal isMounted={dateWarning.isOpen}>
        <Popup
          title={t("Close date is in the past")}
          description={t("Current close date is in the past, continue?")}
          open={dateWarning.isOpen}
          closable
          cancelText={t("Cancel")}
          onCancel={closeDateWarning}
          okText={t("Continue")}
          onOk={saveTeeSheetSettings}
          type="warning"
          stacked
        />
      </Portal>
    </Page>
  );
}
